import { useState, useMemo } from "react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff, Edit, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { UsersList, ManageUserProps } from "../../types/types";
// import { TableHead, TableCell, TableRow } from "@mui/material";

const AddUserTable = ({ users, onEdit, onDelete }: ManageUserProps) => {
  const { t } = useTranslation();
  const [visibleUsernames, setVisibleUsernames] = useState<Set<string>>(
    new Set()
  );

  const toggleUsernameVisibility = (username: string) => {
    setVisibleUsernames((prevVisibleUsernames) => {
      const newVisibleUsernames = new Set(prevVisibleUsernames);
      if (newVisibleUsernames.has(username)) {
        newVisibleUsernames.delete(username);
      } else {
        newVisibleUsernames.add(username);
      }
      return newVisibleUsernames;
    });
  };

  const columns = useMemo<MRT_ColumnDef<UsersList>[]>(
    () => [
      {
        accessorKey: "firstName",
        header: t("first_name"),
        size: 150,
      },
      {
        accessorKey: "lastName",
        header: t("last_name"),
        size: 150,
      },
      {
        accessorKey: "userName",
        header: t("username"),
        size: 200,
        Cell: ({ row }) => {
          const userName = row.original.userName;
          const isUsernameVisible = visibleUsernames.has(userName);
          return (
            <span>
              {isUsernameVisible ? userName : "••••••"}
              <IconButton
                onClick={() => toggleUsernameVisibility(userName)}
                sx={{ padding: 0, marginLeft: 1 }}
              >
                {isUsernameVisible ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </span>
          );
        },
      },
      {
        accessorKey: "locationClass",
        header: t("location_class"),
        size: 200,
      },
      {
        accessorKey: "locationName",
        header: t("location_name"),
        size: 150,
      },
      {
        accessorKey: "accountType",
        header: t("account_type"),
        size: 150,
      },

      {
        id: "actions",
        header: t("actions"),
        Cell: ({ row }) => (
          <div>
            <IconButton
              color="primary"
              onClick={() => onEdit(row.original.id)}
              sx={{ padding: 0 }}
            >
              <Edit />
            </IconButton>
            <IconButton
              className="deleteicon"
              onClick={() => onDelete(row.original.id)}
              sx={{ padding: 0, marginLeft: 1 }}
            >
              <Delete />
            </IconButton>
          </div>
        ),
        size: 100,
      },
    ],
    [visibleUsernames, onEdit, onDelete]
  );

  return (
    <div>
      <MaterialReactTable
        columns={columns}
        data={users}
        enableDensityToggle={true}
        enableHiding={true}
        enableColumnFilters={true}
        enableGlobalFilter={true}
        enableFullScreenToggle={true}
        enableColumnActions={true}
        initialState={{
          showGlobalFilter: true,
        }}
        positionGlobalFilter="left"
        muiSearchTextFieldProps={{
          placeholder: "Search users...",
          sx: { minWidth: "300px" },
          variant: "outlined",
          size: "small",
        }}
        muiTableHeadCellProps={{
          sx: {
            padding: "4px 16px",
            backgroundColor: "#f5f5f5",
            fontWeight: 600,
            lineHeight: "1.2",
          },
        }}
        muiTableProps={{
          sx: {
            "& .MuiTableHead-root": {
              "& .MuiTableRow-root": {
                height: "32px",
              },
            },
            "& tr:nth-of-type(odd)": {
              backgroundColor: "#ffffff !important",
            },
            "& tr:nth-of-type(even)": {
              backgroundColor: "#f5f5f5 !important",
            },
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            height: "40px",
            "&:hover": {
              backgroundColor: "#f0f0f0 !important",
            },
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            padding: "8px 16px",
            borderBottom: "1px solid #e0e0e0",
          },
        }}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            size: 100,
            enableColumnActions: false,
          },
        }}
      />
    </div>
  );
};

export default AddUserTable;
